import { IOptionalPagination, IPagedListResponse } from '@edgebox/data-definition-kit';
import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { SELECTABLE_TERM_STATUSES } from '../../../Components/termStatusPresentation';
import { ClientTaxonomyEntity, ClientTaxonomyTermEntity, ISyncCoreApi, ITaxonomyTermFilters } from '@edgebox/sync-core-rest-client';

/**
 * The one client call the per-facet term cache makes, as a typed slice of the API surface.
 *
 * Narrow on purpose: a surface that only needs terms can be handed exactly this, so its own tests
 * build a plain object with no cast, while the compiler still checks the real `ISyncCoreApi` against
 * it at every call site.
 */
export interface ITaxonomyTermFacetSource {
  service: {
    taxonomyTerms: {
      list: (pagination?: IOptionalPagination, filters?: ITaxonomyTermFilters) => Promise<IPagedListResponse<ClientTaxonomyTermEntity>>;
    };
  };
}

/** The selectable terms of each facet asked for, sorted by name. */
export type TaxonomyTermsByFacet = ReadonlyMap<TaxonomyFacet, ClientTaxonomyTermEntity[]>;

/**
 * Terms per request.
 *
 * Large enough that the facets one screen shows arrive in a single response; a larger set is read
 * page by page to its end, so no term is ever left out.
 */
const TERM_LIST_PAGE_SIZE = 2_000;

let taxonomiesCache: ClientTaxonomyEntity[] | undefined;
let taxonomiesPromise: Promise<ClientTaxonomyEntity[]> | undefined;

const taxonomyTermsCacheByFacet = new Map<TaxonomyFacet, ClientTaxonomyTermEntity[]>();
/** A facet's request on its way, and whether that request carries other facets with it. */
interface PendingTermRequest {
  request: Promise<ClientTaxonomyTermEntity[]>;
  together: boolean;
}
const taxonomyTermsPromiseByFacet = new Map<TaxonomyFacet, PendingTermRequest>();

/** Facets asked for since the last request went out, per client: the next request carries them all. */
interface QueuedTermRequest {
  facets: Set<TaxonomyFacet>;
  response: Promise<TaxonomyTermsByFacet>;
}
const queuedTermRequests = new Map<ITaxonomyTermFacetSource, QueuedTermRequest>();

/**
 * The facets each tag scope on screen declares, one entry per mounted scope: a read of any of them
 * travels with the other reads of the same pass and brings every other facet of its scope that is
 * not loaded yet along. A facet no scope declares is asked for on its own, at once.
 */
const declaredScopes: (readonly TaxonomyFacet[])[] = [];

/**
 * Declares a tag scope's facets for as long as the scope is on screen; the returned function
 * withdraws the declaration.
 */
export function declareTaxonomyTermScope(facets: readonly TaxonomyFacet[]): () => void {
  declaredScopes.push(facets);
  return () => {
    const index = declaredScopes.indexOf(facets);
    if (index >= 0) {
      declaredScopes.splice(index, 1);
    }
  };
}

/**
 * Drops every cached taxonomy and facet-term list. Exported for tests, which need each case to
 * start from nothing — including the scoped taxonomy term index, which now reads its per-facet data
 * through this same cache rather than keeping one of its own.
 */
export function resetCachedTaxonomyData(): void {
  taxonomiesCache = undefined;
  taxonomiesPromise = undefined;
  taxonomyTermsCacheByFacet.clear();
  taxonomyTermsPromiseByFacet.clear();
  queuedTermRequests.clear();
}

/**
 * Forgets the cached tags of the given facets, so the next read asks for them again. A screen that
 * creates a tag calls it: every other screen of the frame reads that facet through this cache for the
 * rest of the session, and would otherwise render the new tag by its key.
 */
export function forgetCachedTaxonomyTerms(facets: readonly TaxonomyFacet[]): void {
  for (const facet of facets) {
    taxonomyTermsCacheByFacet.delete(facet);
    taxonomyTermsPromiseByFacet.delete(facet);
  }
}

export async function getCachedTaxonomies(api: ISyncCoreApi): Promise<ClientTaxonomyEntity[]> {
  if (taxonomiesCache) {
    return taxonomiesCache;
  }

  // Only an answer is cached. A failed request is forgotten, so the next caller asks again instead
  // of receiving the same rejection for as long as the frame stays open.
  const request =
    taxonomiesPromise ?? api.service.taxonomies.list({ page: 0, itemsPerPage: 500 }, undefined).then((result) => result.items || []);
  taxonomiesPromise = request;

  try {
    taxonomiesCache = await request;
    return taxonomiesCache;
  } finally {
    if (taxonomiesPromise === request) {
      taxonomiesPromise = undefined;
    }
  }
}

function byName(a: ClientTaxonomyTermEntity, b: ClientTaxonomyTermEntity): number {
  return String(a.name || '').localeCompare(String(b.name || ''));
}

/**
 * Every selectable term of the given facets, in one request, read page by page to the end.
 *
 * Archived terms are excluded at the source: these lists back filter dropdowns and name lookups,
 * and archived terms are the largest population, so leaving them in would multiply what every read
 * transfers for terms nothing offers.
 */
async function fetchTaxonomyTermsByFacet(api: ITaxonomyTermFacetSource, facets: readonly TaxonomyFacet[]): Promise<TaxonomyTermsByFacet> {
  const termsByFacet = new Map<TaxonomyFacet, ClientTaxonomyTermEntity[]>(facets.map((facet) => [facet, []]));
  // A request for one facet is answered with that facet's terms only, so its items need no sorting
  // into facets; the request itself names the facet exactly as a request for a single list always has.
  const [onlyFacet] = facets.length === 1 ? facets : [];
  const facetOf = (term: ClientTaxonomyTermEntity): TaxonomyFacet | undefined => onlyFacet ?? term.taxonomy?.facet;

  let numberOfPages = 1;
  for (let page = 0; page < numberOfPages; page += 1) {
    const response = await api.service.taxonomyTerms.list(
      { page, itemsPerPage: TERM_LIST_PAGE_SIZE },
      { taxonomyFacet: onlyFacet ?? [...facets], statuses: [...SELECTABLE_TERM_STATUSES] }
    );

    const items = response.items || [];
    for (const term of items) {
      const facet = facetOf(term);
      if (facet) {
        termsByFacet.get(facet)?.push(term);
      }
    }

    // The page count is read from each answer, which counts the pages at the size the endpoint
    // actually served, so a page cut shorter than asked for is followed by the next one rather
    // than taken for the last.
    numberOfPages = response.numberOfPages ?? 1;
    if (!items.length) {
      break;
    }
  }

  for (const terms of termsByFacet.values()) {
    terms.sort(byName);
  }
  return termsByFacet;
}

/**
 * Adds a facet to the request the current task is collecting for this client, and answers with the
 * response it will get.
 *
 * The request goes out once the code running now has finished, so every facet asked for in the
 * meantime travels in it: a screen whose effects and scope all ask for their facets in the same
 * pass costs one request, not one per facet.
 */
function queueTermRequest(api: ITaxonomyTermFacetSource, facet: TaxonomyFacet): Promise<TaxonomyTermsByFacet> {
  let queued = queuedTermRequests.get(api);
  if (!queued) {
    const collected = new Set<TaxonomyFacet>();
    const next: QueuedTermRequest = {
      facets: collected,
      // A resolved promise holds the send until the code running now has finished, without a timer,
      // so nothing waits on a clock — test doubles of the timers included.
      response: Promise.resolve().then(() => {
        if (queuedTermRequests.get(api) === next) {
          queuedTermRequests.delete(api);
        }
        return fetchTaxonomyTermsByFacet(api, Array.from(collected));
      }),
    };
    queuedTermRequests.set(api, next);
    queued = next;
  }

  queued.facets.add(facet);
  return queued.response;
}

/**
 * One facet's selectable terms, sorted by name: the cached list, the request already on its way, or
 * a new request — a place in the next one when the facet travels `together` with others, which then
 * also carries every facet not loaded yet of each tag scope on screen that declares this one, or a
 * request of its own, sent at once, otherwise.
 *
 * Only an answer is cached. A failed request is forgotten for each facet it carried, so the next
 * caller asks again instead of receiving the same rejection for as long as the frame stays open.
 */
function cachedOrRequestedTerms(
  api: ITaxonomyTermFacetSource,
  { facet, together }: { facet: TaxonomyFacet; together: boolean }
): Promise<ClientTaxonomyTermEntity[]> {
  const cached = taxonomyTermsCacheByFacet.get(facet);
  if (cached) {
    return Promise.resolve(cached);
  }

  // A read of its own never waits on a request that carries other facets: a screen without a scope
  // keeps its own requests — their time and their failures — even while another screen's scope
  // request for the same facet is still on its way. It asks again, alone, and its answer is kept.
  const pending = taxonomyTermsPromiseByFacet.get(facet);
  if (pending && (together || !pending.together)) {
    return pending.request;
  }

  const response = together ? queueTermRequest(api, facet) : fetchTaxonomyTermsByFacet(api, [facet]);
  // Every facet the request was asked for has its entry in the answer, an empty one included.
  const request = response.then((termsByFacet) => termsByFacet.get(facet) ?? []);
  taxonomyTermsPromiseByFacet.set(facet, { request, together });
  // Registered before anyone else can wait on the request, so the cache is filled by the time a
  // caller continues. It also settles the rejection for a facet no caller is waiting on.
  void request.then(
    (terms) => {
      if (taxonomyTermsPromiseByFacet.get(facet)?.request === request) {
        taxonomyTermsCacheByFacet.set(facet, terms);
        taxonomyTermsPromiseByFacet.delete(facet);
      }
    },
    () => {
      if (taxonomyTermsPromiseByFacet.get(facet)?.request === request) {
        taxonomyTermsPromiseByFacet.delete(facet);
      }
    }
  );

  if (together) {
    // Each of these is registered as on its way before the next is looked at, so a facet two scopes
    // share is asked for once.
    for (const scope of declaredScopes.filter((declared) => declared.includes(facet))) {
      for (const sibling of scope) {
        void cachedOrRequestedTerms(api, { facet: sibling, together: true });
      }
    }
  }
  return request;
}

/**
 * The selectable terms of one facet, sorted by name.
 *
 * Under a tag scope that declares the facet, the read travels with the other reads of the same pass
 * and the scope's facets not loaded yet, in one request; any other facet is asked for on its own.
 */
export function getCachedTaxonomyTermsByFacet(api: ITaxonomyTermFacetSource, facet: TaxonomyFacet): Promise<ClientTaxonomyTermEntity[]> {
  return cachedOrRequestedTerms(api, { facet, together: declaredScopes.some((declared) => declared.includes(facet)) });
}

/** The cached lists of the given facets when every one of them has loaded; asks nothing. */
export function cachedTaxonomyTermsByFacets(facets: readonly TaxonomyFacet[]): TaxonomyTermsByFacet | undefined {
  const termsByFacet = new Map<TaxonomyFacet, ClientTaxonomyTermEntity[]>();
  for (const facet of facets) {
    const cached = taxonomyTermsCacheByFacet.get(facet);
    if (!cached) {
      return undefined;
    }
    termsByFacet.set(facet, cached);
  }
  return termsByFacet;
}

/**
 * The lists of the given facets from the cache or from the requests already on their way, or
 * undefined when any of them has neither; asks nothing.
 */
export function joinedTaxonomyTermsByFacets(facets: readonly TaxonomyFacet[]): Promise<TaxonomyTermsByFacet> | undefined {
  const lists: Promise<ClientTaxonomyTermEntity[]>[] = [];
  for (const facet of facets) {
    const cached = taxonomyTermsCacheByFacet.get(facet);
    const known = cached ? Promise.resolve(cached) : taxonomyTermsPromiseByFacet.get(facet)?.request;
    if (!known) {
      return undefined;
    }
    lists.push(known);
  }
  return Promise.all(lists).then((loaded) => new Map(facets.map((facet, index) => [facet, loaded[index]])));
}

/**
 * The selectable terms of several facets: every one not cached or on its way travels in ONE new
 * request, together with the other reads of the same pass.
 */
export function getCachedTaxonomyTermsByFacets(
  api: ITaxonomyTermFacetSource,
  facets: readonly TaxonomyFacet[]
): Promise<TaxonomyTermsByFacet> {
  const uniqueFacets = Array.from(new Set(facets));
  return Promise.all(uniqueFacets.map((facet) => cachedOrRequestedTerms(api, { facet, together: true }))).then(
    (lists) => new Map(uniqueFacets.map((facet, index) => [facet, lists[index]]))
  );
}
